import { useState } from 'react';
import { observer } from 'mobx-react';
import useAsyncEffect from 'use-async-effect';
import { useNavigate } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import { View } from '../../../../core/components/Index';
import logo from '../../../../assets/images/logo_primary.svg';
import RetirementCalcViewModel from './RetirementCalcViewModel';
import { exactAgeText, formatEstimateGeneratedTimestamp, money, parseDate, vbRound2 } from './engine/RetirementCalcEngine';
import { saveEstimateAsPdf } from './RetirementEstimatePdf';
import './RetirementCalcView.css';
import './RetirementResultsView.css';

const fmtDate = (d: Date | null) => d
  ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

const NOTE = 'Once your beneficiary is designated to receive a continuing monthly benefit (also known as a continuance), this designation cannot be changed after retirement—even if your circumstances change.';

const RetirementResultsView = observer(() => {
  const vm = Appl.Services.get<RetirementCalcViewModel>('RetirementCalcViewModel');
  vm.navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [generatedAt] = useState(() => new Date());

  useAsyncEffect(async () => {
    // Nothing to show (direct visit / expired session) — back to the form.
    if (!(await vm.ensureResultAsync())) {
      vm.navigate('/retirement-calculator');
      return;
    }
    setReady(true);
  }, []);

  const res = vm.result;
  const r = res?.components[0];
  if (!ready || !res || !r) {
    return <View id="retirement-results" pageTitle="Retirement Estimate" container={false} className=""><div className="retirement-calc" /></View>;
  }

  const f = vm.form;
  const dob = parseDate(f.dob as string | undefined);
  const ret = parseDate(f.ret as string | undefined);
  const benDob = f.includeBen ? parseDate(f.benDob as string | undefined) : null;
  const hasBen = f.includeBen && !!benDob;
  const isInsurable = res.isInsurable;
  const relationshipCode = isInsurable ? 'insurable' : 'spouse';
  const relationshipLabel = vm.referenceData.beneficiaryRelationshipOptions.find(o => o.code === relationshipCode)?.label
    ?? (isInsurable ? 'Insurable Interest' : 'Spouse / Domestic Partner');
  const multi = res.components.length > 1;
  const unmodCont = res.continuance !== null ? money(res.continuance) : '—';
  const opt2Pct = res.opt2Pct;
  const opt2Cont = res.opt2Cont !== null ? money(res.opt2Cont) : '—';
  const opt3Cont = res.opt3Cont !== null ? money(res.opt3Cont) : '—';

  const editScenario = () => { vm.navigate(vm.formPath); };
  const startOver = () => { vm.resetForm(); vm.navigate('/retirement-calculator'); };
  const download = async () => {
    setBusy(true); setPdfError(null);
    try {
      await saveEstimateAsPdf('estimateExport', ret);
    } catch (e) {
      console.error('PDF generation error', e);
      setPdfError('The PDF could not be created in this browser. You can still use Print Estimate and choose Save as PDF from the print options.');
    } finally {
      setBusy(false);
    }
  };

  const rows: Array<[string, string]> = [
    [multi ? 'Current / Most Recent Plan / Tier' : 'Current Plan / Tier', r.cfg.name],
    ['Retirement Date', fmtDate(ret)],
    ['Age at Retirement', dob && ret ? exactAgeText(dob, ret) : '—'],
    ['Total Service Credit Used', `${res.service.toFixed(4)} years`],
    ['Prior Memberships', multi ? res.components.slice(1).map(c => c.cfg.name).join(', ') : 'None'],
    ['Final Monthly Compensation', money(f.comp ?? 0)],
    ['Date of Birth', fmtDate(dob)],
    ['Unused Sick Leave', `${r.b.mbaseDetail ? Number(f.mbaseIntegratedSickHours ?? 0) + Number(f.mbaseNonIntegratedSickHours ?? 0) : (f.sickHours ?? 0)} hours`],
    ['Beneficiary Relationship', hasBen ? relationshipLabel : 'None'],
    ['Beneficiary DOB', hasBen ? fmtDate(benDob) : '—'],
    ['Temporary Annuity Included', f.includeTA ? 'Yes' : 'No'],
    ['Estimated Social Security Benefit', f.includeTA && f.ssEstimate ? money(f.ssEstimate) : '—'],
  ];

  const options: Array<{ name: string; sub: string; amount: string; cont: string; ta?: { before: number; after: number } | null; body: JSX.Element }> = [
    {
      name: 'Unmodified Allowance', sub: '60% Continuance to Surviving Spouse or Domestic Partner',
      amount: money(res.unmod), cont: unmodCont, ta: res.ta?.unmod,
      body: (<>
        <p><strong>Benefit paid to Retired Member:</strong></p>
        <ul><li>Maximum allowance provided for Retired Member's lifetime, based on Age, Service Credit, and Final Compensation.</li></ul>
        <p><strong>Benefit paid to Beneficiary when Retired Member dies:</strong></p>
        <ul>
          <li>For spouse/registered domestic partner who was married to/registered with Retired Member for at least one year before retirement date, 60% of Retired Member's monthly allowance at time of death (100% of Service-Connected Disability Retirement allowance), payable for Beneficiary's lifetime.</li>
          <li>If no eligible spouse/registered domestic partner, minor child(ren) receive, collectively, a monthly allowance that is 60% of Retired Member's Service Retirement allowance at time of death (100% of Service-Connected Disability Retirement allowance). Payable until minor child(ren) reaches age 18 (or reaches age 22 if regularly enrolled as a full-time student) or marries, whichever occurs first.</li>
          <li>For someone other than a spouse/registered domestic partner or minor child, one-time, lump-sum payment of Retired Member's Member Contributions, minus the monthly retirement allowance payments already paid by SCERS.</li>
        </ul>
        <p><strong>Please Note:</strong> {NOTE}</p>
      </>),
    },
    ...(res.components.some(c => c.o1) ? [{
      name: 'Option 1', sub: 'Lump Sum Benefit to Beneficiary; No Monthly Continuance',
      amount: money(res.opt1), cont: '—', ta: res.ta?.opt1,
      body: (<>
        <p><strong>Benefit paid to Retired Member:</strong></p>
        <ul><li>Slightly lower allowance for Retired Member's lifetime than under Unmodified Allowance.</li></ul>
        <p><strong>Benefit paid to Beneficiary when Retired Member dies:</strong></p>
        <ul><li>One-time, lump-sum payment of Retired Member's Member Contributions, minus the Annuity portion of monthly payments already paid by SCERS. May leave a higher lump-sum balance to be paid to Beneficiary than under Unmodified Allowance because contribution balance is drawn down by monthly payments to Retired Member at a slower rate.</li></ul>
        <p>{res.components.some(c => c.o1 && c.b.mbaseDetail && c.o1.employeeBalance !== 6000)
          ? 'This Option 1 estimate uses the MBASE/RAP Regular Employee Contributions value (excluding COLA) and the applicable Option 1 factor table.'
          : 'This Option 1 amount is an estimate based on the guest-calculator methodology.'}</p>
      </>),
    }] : []),
    ...(res.opt2 !== null ? [{
      name: 'Option 2', sub: `100% Joint and Survivor Annuity${opt2Pct < 100 ? '*' : ''}`, amount: money(res.opt2 as number), cont: opt2Cont, ta: res.ta?.opt2,
      body: (<>
        <p><strong>Benefit paid to Retired Member:</strong></p>
        <ul><li>Reduced allowance compared to Unmodified Allowance, with amount of reduction based on Beneficiary's age.</li></ul>
        <p><strong>Benefit paid to Beneficiary when Retired Member dies:</strong></p>
        <ul><li>{opt2Pct}% of Retired Member's monthly allowance at time of death, payable for Beneficiary's lifetime{isInsurable ? ' (maximum continuance for this Insurable Interest beneficiary)' : ''}.</li></ul>
        {opt2Pct < 100 && <p>* Option 2 normally provides a 100% survivor continuance. Because of the age difference between you and your beneficiary, this estimate uses an adjusted {opt2Pct}% continuance.</p>}
        <p><strong>Please Note:</strong> {NOTE}</p>
      </>),
    }] : []),
    ...(res.opt3 !== null ? [{
      name: 'Option 3', sub: '50% Joint and Survivor Annuity', amount: money(res.opt3 as number), cont: opt3Cont, ta: res.ta?.opt3,
      body: (<>
        <p><strong>Benefit paid to Retired Member:</strong></p>
        <ul><li>Reduced allowance compared to Unmodified Allowance, with amount of reduction based on Beneficiary's age.</li></ul>
        <p><strong>Benefit paid to Beneficiary when Retired Member dies:</strong></p>
        <ul><li>50% of Retired Member's monthly allowance at time of death, payable for Beneficiary's lifetime.</li></ul>
        <p><strong>Please Note:</strong> {NOTE}</p>
      </>),
    }] : []),
  ];

  const componentRows = (c: typeof r, prefix: string): Array<[string, string]> => [
    [`${prefix}Plan / Tier`, c.cfg.name],
    [`${prefix}Service Credit Used`, `${c.service.toFixed(5)} years`],
    [`${prefix}Benefit Factor`, `${c.factor.toFixed(3)}%`],
    [`${prefix}Retirement Factor (ERF)`, c.erf.toString()],
    [`${prefix}Regular Service Credit`, `${c.b.regular.toFixed(5)} years`],
    [`${prefix}Sick Leave Service Credit`, `${c.b.sick.toFixed(5)} years`],
    [`${prefix}Unmodified Allowance`, money(c.b.rounded)],
  ];
  const detailRows: Array<[string, string]> = [
    ['Age at Retirement (quarter-year basis)', r.age.toFixed(2)],
    ...(multi
      ? res.components.flatMap((c, i) => componentRows(c, i === 0 ? 'Current: ' : `Prior ${i}: `))
      : componentRows(r, '')),
    ...(multi ? [['Combined Service Credit', `${res.service.toFixed(5)} years`], ['Combined Unmodified Allowance', money(res.unmod)]] as Array<[string, string]> : []),
  ];

  return (
    <View id="retirement-results" pageTitle="Retirement Estimate" container={false} className="">
      <div className="retirement-calc retirement-results">
        <div id="estimateExport" className="results-page">
          <div className="results-logo"><img src={logo} alt="SCERS" /></div>
          <div className="results-eyebrow">Retirement Benefit Calculator</div>
          <h1 className="results-title">Your Estimated Retirement Benefit</h1>
          <p className="results-sub no-pdf">Review this scenario, compare payment options, or change your assumptions and recalculate.</p>
          <p className="results-stamp">{formatEstimateGeneratedTimestamp(generatedAt)}</p>

          <div className="hero-grid">
            <div className="hero-card">
              <div className="hero-label">Estimated Monthly Unmodified Allowance</div>
              <div className="hero-amount">{money(res.unmod)}</div>
              <div className="hero-note">Estimated monthly benefit before taxes and applicable deductions.</div>
              {res.continuance !== null && (
                <div className="hero-cont">
                  <div className="hero-label">Eligible Survivor Continuance</div>
                  <div><b>60%</b> &nbsp;·&nbsp; <span className="hero-cont-amt">{money(res.continuance as number)}/month</span></div>
                </div>
              )}
            </div>
            <div className="scenario-card">
              <div className="scenario-head">
                <h2>Scenario Details</h2>
                <button type="button" className="pill-btn no-pdf" onClick={editScenario}>Edit Scenario</button>
              </div>
              <dl className="scenario-grid">
                {rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
              </dl>
            </div>
          </div>

          <section className="panel">
            <h2>Compare Payment Options</h2>
            <p className="panel-sub">Compare the monthly amounts at a glance, then expand an option to learn how it works.</p>
            {options.map((o, i) => (
              <details key={o.name} className={`option-acc${i === 0 ? ' featured' : ''}`}>
                <summary>
                  <span className="opt-name">{o.name}<small>{o.sub}</small></span>
                  <span className="opt-metric"><small>Monthly Allowance</small><strong>{o.amount}</strong></span>
                  <span className="opt-metric"><small>Survivor Continuance</small><strong>{o.cont}</strong></span>
                  {o.ta && (
                    <span className="ta-breakdown" aria-label={`Temporary Annuity amounts for ${o.name}`}>
                      <span className="ta-label">Temporary Annuity</span>
                      <span className="opt-metric"><small>Before Age 62</small><strong>{money(o.ta.before)}</strong></span>
                      <span className="opt-metric"><small>Beginning At Age 62</small><strong>{money(o.ta.after)}</strong></span>
                    </span>
                  )}
                </summary>
                <div className="opt-detail">{o.body}</div>
              </details>
            ))}
          </section>

          <section className="panel">
            <h2>Estimate Details</h2>
            <p className="panel-sub">Review the information and calculation details used for this estimate.</p>
            <details className="details-toggle" open={undefined}>
              <summary>View Estimate Details</summary>
              <dl className="detail-list">
                {detailRows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
              </dl>
            </details>
            <p className="result-disclaimer">This is a preliminary estimate only. Your actual benefit is determined at retirement based on your final compensation, service credit, and SCERS rules in effect at that time.</p>
          </section>
        </div>

        {pdfError && <div className="load-error-banner calc-error-banner">{pdfError}</div>}
        <div className="results-actions">
          <button type="button" className="outline-btn" disabled={busy} onClick={download}>{busy ? 'Preparing PDF…' : 'Download Estimate'}</button>
          <button type="button" className="outline-btn" onClick={() => window.print()}>Print Estimate</button>
        </div>
        <div className="another-card">
          <div><strong>Want to test another scenario?</strong><br /><small>Change your current scenario or start again with new information.</small></div>
          <div className="another-actions">
            <button type="button" className="solid-btn" onClick={editScenario}>Edit Scenario</button>
            <button type="button" className="outline-btn orange" onClick={startOver}>Start Over</button>
          </div>
        </div>
      </div>
    </View>
  );
});

export default RetirementResultsView;
